/**
 * useLoadProfile - Custom hook for managing load profile state and operations
 * 
 * Handles:
 * - Load profile data (8760 hourly values)
 * - CSV parsing and upload
 * - Load model generation
 * - Profile summarization
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import { API_BASE_URL } from '../services/api';



/**
 * Summarize an 8760 load array
 */
function summarizeLoads(arr) {
    if (!Array.isArray(arr) || arr.length === 0) {
        return { total: 0, peak: 0, loadFactor: 0 };
    }
    const total = arr.reduce((a, b) => a + b, 0);
    const peak = Math.max(...arr);
    const loadFactor = peak ? total / (peak * 8760) : 0;
    return { total, peak, loadFactor };
}

/**
 * Parse an uploaded CSV containing hourly (8760) or 15-min (35040)
 * kW load values.
 */
function parseLoadCsv(text) {
    const lines = text
        .trim()
        .split(/\r?\n/)
        .filter((l) => l.trim());
    let year = 2017;
    const values = [];

    for (const line of lines) {
        const parts = line.split(/,|\s+/).filter(Boolean);
        if (!parts.length) continue;
        const maybeDate = parts[0];
        const maybeVal = parts.length > 1 ? parts[1] : parts[0];
        const num = parseFloat(maybeVal);
        if (!isNaN(num)) {
            values.push(num);
        }
        const dt = Date.parse(maybeDate);
        if (!isNaN(dt)) {
            year = new Date(dt).getFullYear();
        }
    }

    if (values.length === 35040) {
        const hourly = [];
        for (let i = 0; i < values.length; i += 4) {
            hourly.push(
                (values[i] + values[i + 1] + values[i + 2] + values[i + 3]) / 4,
            );
        }
        return { year, loads: hourly };
    }
    if (values.length === 8760) {
        return { year, loads: values };
    }
    throw new Error("Expected 8760 hourly or 35040 15-min values");
}

/**
 * Custom hook for load profile management
 */
export function useLoadProfile() {
    // Initial flat load profile
    const initialLoads = useMemo(
        () => [],
        [],
    );

    // Core load state
    // "Base" load is the facility load (from CSV or basic model)
    const [baseLoads, setBaseLoads] = useState(initialLoads);

    // "EV" load is the additive load from the EV model
    const [evLoads, setEvLoads] = useState(initialLoads); // Default is 0 array actually, but safe to init with baseline for now or 0s
    const [isEvEnabled, setIsEvEnabled] = useState(false);
    const [evLoadSource, setEvLoadSource] = useState(null);

    // Version tracking to force dirty detection when evLoads changes
    const [loadProfileVersion, setLoadProfileVersion] = useState(0);

    // "Existing Solar" is the subtractive production from existing solar assets
    const [existingSolarProfile, setExistingSolarProfile] = useState([]);
    const [existingSolarParams, setExistingSolarParams] = useState({});
    const [baseLoadIsNetOfSolar, setBaseLoadIsNetOfSolar] = useState(false);
    const [existingSolarCanExport, setExistingSolarCanExport] = useState(false);

    // Derived total load (Net Load = Base + EV - Existing Solar)
    // If baseLoadIsNetOfSolar = true, base load already has solar subtracted, so we DON'T subtract again
    // If baseLoadIsNetOfSolar = false, base load is gross, so we DO subtract solar
    const loads = useMemo(() => {
        // Start with base loads if available, otherwise use EV loads if available
        let result = [];

        if (baseLoads.length === 8760) {
            result = [...baseLoads];
        } else if (isEvEnabled && evLoads.length === 8760) {
            // No base load, but we have EV - start with EV loads
            result = [...evLoads];
        }

        // Add EV load if enabled and we started with base loads
        if (isEvEnabled && evLoads.length === 8760 && result.length === 8760 && baseLoads.length === 8760) {
            result = result.map((val, idx) => val + (evLoads[idx] || 0));
        }

        // Subtract existing solar production ONLY if base load is NOT already net of solar
        if (!baseLoadIsNetOfSolar && existingSolarProfile.length === 8760 && result.length === 8760) {
            result = result.map((val, idx) => {
                const netLoad = val - (existingSolarProfile[idx] || 0);
                // If export is NOT enabled, clamp to zero (no export revenue modeled)
                // If export IS enabled, allow negative values (export to grid)
                return existingSolarCanExport ? netLoad : Math.max(0, netLoad);
            });
        }

        return result;
    }, [baseLoads, evLoads, isEvEnabled, existingSolarProfile, baseLoadIsNetOfSolar, existingSolarCanExport]);

    // Calculate gross load (facility load before solar offset)
    // If base is already net, add solar back. Otherwise base is already gross.
    const grossLoad = useMemo(() => {
        if (baseLoadIsNetOfSolar && existingSolarProfile.length === 8760 && baseLoads.length === 8760) {
            // Base is net, so gross = base + solar
            return baseLoads.map((val, idx) => val + (existingSolarProfile[idx] || 0));
        }
        // Base is already gross, return as-is
        return baseLoads;
    }, [baseLoads, existingSolarProfile, baseLoadIsNetOfSolar]);

    // Standard state
    const [loadYear, setLoadYear] = useState(String(new Date().getFullYear()));
    const [loadSource, setLoadSource] = useState("default");
    const [loadFileName, setLoadFileName] = useState("");
    const [loadTab, setLoadTab] = useState(0);

    // Load model configuration
    const [loadModel, setLoadModel] = useState({ type: '', params: {} });
    const [useAdvancedLoadModeling, setUseAdvancedLoadModeling] = useState(false);



    // Critical load
    const [criticalLoadFraction, setCriticalLoadFraction] = useState(null);

    // Derived values
    const loadSummary = useMemo(() => summarizeLoads(loads), [loads]);

    const startDate = useMemo(
        () => new Date(parseInt(loadYear, 10) || 0, 0, 1),
        [loadYear],
    );

    // Handle CSV file upload via backend
    const handleLoadFile = useCallback(async (file, onSuccess, onError) => {
        if (!file) return;

        try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await fetch(`${API_BASE_URL}/api/site_dna/v1/load-profile/upload`, {
                method: 'POST',
                body: formData,
                headers: {
                    'X-Frontend-Source': 'react-frontend'
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to process CSV file');
            }

            const result = await response.json();

            if (!result.success) {
                throw new Error(result.message || 'CSV processing failed');
            }

            // Update state with processed data
            setLoadYear(String(result.year));
            setBaseLoads(result.loads_kw); // CSV upload sets BASE load
            setLoadSource("csv_upload");
            setLoadFileName(file.name);

            if (onSuccess) {
                onSuccess(result);
            }
        } catch (err) {
            setLoadFileName("");
            if (onError) {
                onError(err);
            }
        }
    }, []);

    // Remove uploaded file and reset to default
    const handleRemoveFile = useCallback(() => {
        setLoadFileName("");
        setBaseLoads(initialLoads);
        if (!isEvEnabled) setLoadSource("default");
    }, [initialLoads, isEvEnabled]);

    // Import EV load from Flux EV project
    const importFluxEVLoad = useCallback((loads_kw, sourceInfo) => {
        if (!Array.isArray(loads_kw) || loads_kw.length !== 8760) {
            throw new Error('Invalid load profile: expected 8760 hourly values');
        }

        setEvLoads(loads_kw);
        setIsEvEnabled(true);
        setEvLoadSource(sourceInfo);
    }, []);

    // Increment version when evLoads changes to trigger dirty tracking and auto-save
    useEffect(() => {
        if (evLoads.length > 0) {
            setLoadProfileVersion(prev => prev + 1);
        }
    }, [evLoads]);

    const removeEVLoad = useCallback(() => {
        setEvLoads([]);
        setIsEvEnabled(false);
        setEvLoadSource(null);
    }, []);


    // Generate profile from load model
    const generateLoadModelProfile = useCallback(async (onSuccess, onError) => {
        if (!loadModel.type) {
            onError?.(new Error("Please select a load model type"));
            return;
        }

        try {
            const generatedLoads = await generateClientSideProfile(loadModel.type, loadModel.params);

            setLoadYear(String(loadModel.params.year || new Date().getFullYear()));

            // If it's EV model, set EV loads. Otherwise set Base loads (e.g. cold storage)
            // Or should all LoadModelSelector usage go to EV buffer?
            // Current user request implies splitting Facility (Base) and EV.
            // But LoadModelSelector supports non-EV models too.
            // For now, if type is ev_charging, use EV buffer. Else Base buffer.

            if (loadModel.type === 'ev_charging') {
                setEvLoads(generatedLoads);
                setIsEvEnabled(true);
                // Don't overwrite base load source if we are just adding EV
                if (loadSource === 'default') setLoadSource(`load_model:${loadModel.type}`);
            } else {
                setBaseLoads(generatedLoads);
                setLoadFileName(`${loadModel.type.replace('_', ' ')} Model Generated`);
                setLoadSource(`load_model:${loadModel.type}`);
            }


            if (onSuccess) {
                onSuccess(generatedLoads);
            }
        } catch (err) {
            if (onError) {
                onError(err);
            }
        }
    }, [loadModel]);

    // Client-side profile generation (for preview)
    const generateClientSideProfile = async (modelType, params) => {
        switch (modelType) {
            case 'enrichment':
                return generateEnrichmentProfile(params);
            case 'cold_storage':
                return generateColdStorageProfile(params);
            case 'ev_charging':
                return await generateEVChargingProfile(params);
            default:
                throw new Error(`Unknown model type: ${modelType}`);
        }
    };

    const generateEnrichmentProfile = (params) => {
        const arr = [];
        const peakKw = (params.plant_swu_per_year * params.kwh_per_swu) / 8760;

        for (let hour = 0; hour < 8760; hour++) {
            const dayOfYear = Math.floor(hour / 24);
            const availabilityFactor = Math.random() < (params.availability || 0.96) ? 1.0 : 0.1;
            const seasonalFactor = 1.0 + (params.hvac_seasonal_amplitude || 0.1) *
                Math.cos(2 * Math.PI * (dayOfYear - ((params.season_peak_month || 7) * 30.44)) / 365);
            const centrifugePower = peakKw * availabilityFactor;
            const hvacPower = centrifugePower * (params.hvac_fraction_of_running || 0.3) * seasonalFactor;
            const totalPower = centrifugePower + hvacPower + (params.aux_kw_constant || 100);
            arr.push(Math.max(0, totalPower));
        }
        return arr;
    };

    const generateColdStorageProfile = (params) => {
        const arr = [];
        const baseLoad = (params.storage_capacity_cf / 1000) * (params.kw_per_1000_cf || 15);

        for (let hour = 0; hour < 8760; hour++) {
            const dayOfYear = Math.floor(hour / 24);
            const hourOfDay = hour % 24;
            const tempFactor = 1.0 + 0.3 * Math.cos(2 * Math.PI * dayOfYear / 365);
            const defrostCycle = (hourOfDay % 6 === 0) ? 1.5 : 1.0;
            const totalPower = baseLoad * tempFactor * defrostCycle + (params.aux_load_kw || 50);
            arr.push(Math.max(0, totalPower));
        }
        return arr;
    };

    const generateEVChargingProfile = async (params) => {
        // EV fleet modeling has moved to Flux EV app
        // Users should create EV scenarios in Flux EV, then import the load profile here
        throw new Error(
            'EV fleet simulation is now in Flux EV. Please:\n' +
            '1. Open Flux EV app\n' +
            '2. Create a project with your fleet and chargers\n' +
            '3. Run simulation\n' +
            '4. Import the EV load profile back into Site DNA'
        );
    };

    // Reset to default
    const resetLoadProfile = useCallback(() => {
        setBaseLoads(initialLoads);
        setEvLoads(Array(8760).fill(0)); // Reset EV to zero
        setIsEvEnabled(false);
        setEvLoadSource(null);
        setExistingSolarProfile([]);
        setExistingSolarParams({});
        setBaseLoadIsNetOfSolar(false);
        setExistingSolarCanExport(false);
        setLoadYear(String(new Date().getFullYear()));
        setLoadSource("default");
        setLoadFileName("");
        setLoadModel({ type: '', params: {} });
        setUseAdvancedLoadModeling(false);
        setCriticalLoadFraction(null);
    }, [initialLoads]);

    // Serialize for persistence
    const serializeLoadProfile = useCallback(() => {
        return {
            baseLoads,
            evLoads,
            isEvEnabled,
            evLoadSource,
            existingSolarProfile,
            existingSolarParams,
            baseLoadIsNetOfSolar,
            existingSolarCanExport,
            loadYear,
            loadSource,
            loadFileName,
            loadModel,
            useAdvancedLoadModeling,
            criticalLoadFraction,
            loadProfileVersion, // Include version to force dirty detection
        };
    }, [baseLoads, evLoads, isEvEnabled, evLoadSource, existingSolarProfile, existingSolarParams, baseLoadIsNetOfSolar, existingSolarCanExport, loadYear, loadSource, loadFileName, loadModel, useAdvancedLoadModeling, criticalLoadFraction, loadProfileVersion]);

    // Load from saved state
    const loadLoadProfile = useCallback((savedState) => {
        if (!savedState) return;
        if (savedState.baseLoads) setBaseLoads(savedState.baseLoads);
        // Backward compatibility for old saves that only had 'loads'
        if (savedState.loads && !savedState.baseLoads) setBaseLoads(savedState.loads);

        if (savedState.evLoads) setEvLoads(savedState.evLoads);
        if (savedState.isEvEnabled !== undefined) setIsEvEnabled(savedState.isEvEnabled);
        if (savedState.evLoadSource !== undefined) setEvLoadSource(savedState.evLoadSource);

        if (savedState.existingSolarProfile) setExistingSolarProfile(savedState.existingSolarProfile);
        if (savedState.existingSolarParams) setExistingSolarParams(savedState.existingSolarParams);
        if (savedState.baseLoadIsNetOfSolar !== undefined) setBaseLoadIsNetOfSolar(savedState.baseLoadIsNetOfSolar);
        if (savedState.existingSolarCanExport !== undefined) setExistingSolarCanExport(savedState.existingSolarCanExport);

        if (savedState.loadYear) setLoadYear(savedState.loadYear);
        if (savedState.loadSource) setLoadSource(savedState.loadSource);
        if (savedState.loadFileName) setLoadFileName(savedState.loadFileName);
        if (savedState.loadModel) setLoadModel(savedState.loadModel);
        if (savedState.useAdvancedLoadModeling !== undefined) setUseAdvancedLoadModeling(savedState.useAdvancedLoadModeling);

        if (savedState.criticalLoadFraction !== undefined) setCriticalLoadFraction(savedState.criticalLoadFraction);
        if (savedState.loadProfileVersion !== undefined) setLoadProfileVersion(savedState.loadProfileVersion);
    }, []);

    return {
        // Core state
        loads, // Exported as read-only derived sum (net load)
        grossLoad, // Gross load before solar offset
        baseLoads, setBaseLoads,
        evLoads, setEvLoads,
        isEvEnabled, setIsEvEnabled,
        evLoadSource,
        loadYear, setLoadYear,
        loadSource, setLoadSource,
        loadFileName, setLoadFileName,
        loadTab, setLoadTab,

        // Load model
        loadModel, setLoadModel,
        useAdvancedLoadModeling, setUseAdvancedLoadModeling,

        // Existing solar
        existingSolarProfile, setExistingSolarProfile,
        existingSolarParams, setExistingSolarParams,
        baseLoadIsNetOfSolar, setBaseLoadIsNetOfSolar,
        existingSolarCanExport, setExistingSolarCanExport,

        // Critical load
        criticalLoadFraction, setCriticalLoadFraction,

        // Derived
        loadSummary,
        startDate,
        initialLoads,

        // Actions
        handleLoadFile,
        handleRemoveFile,
        importFluxEVLoad,
        removeEVLoad,

        generateLoadModelProfile,
        resetLoadProfile,

        // Persistence
        serializeLoadProfile,
        loadLoadProfile,

        // Utilities
        summarizeLoads,
        parseLoadCsv,

    };
}

export default useLoadProfile;
